import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/data";

const links = [
  { to: "/", label: "Home", id: "nav-home-link" },
  { to: "/about", label: "About", id: "nav-about-link" },
  { to: "/services", label: "Services", id: "nav-services-link" },
  { to: "/contact", label: "Contact", id: "nav-contact-link" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ivory/85 backdrop-blur-xl shadow-[0_8px_40px_-12px_rgba(26,36,33,0.12)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
          <img src="/prg-mark.png" alt="Property Resolutions Group" className="h-12 w-12 object-contain group-hover:scale-105 transition-transform duration-300" />
          <span className="hidden sm:block text-[11px] tracking-[0.22em] uppercase text-charcoal group-hover:text-sage-deep transition-colors duration-300">
            Property Resolutions Group
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors duration-300 ${
                  isActive ? "text-sage-deep font-medium" : "text-charcoal hover:text-night"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-5">
          <a
            href={PHONE_TEL}
            data-testid="nav-phone-link"
            className="flex items-center gap-2 text-sm text-charcoal hover:text-sage-deep transition-colors duration-300"
          >
            <Phone size={15} strokeWidth={1.75} />
            {PHONE_DISPLAY}
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="rounded-full bg-night text-ivory text-sm px-6 py-2.5 hover:bg-sage-deep hover:scale-[1.03] transition-all duration-300"
          >
            Private Conversation
          </Link>
        </div>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="md:hidden text-night p-2"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-ivory/95 backdrop-blur-xl border-t border-sage/20"
            aria-label="Mobile"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-${l.id}`}
                  className="font-serif text-2xl text-night"
                >
                  {l.label}
                </NavLink>
              ))}
              <a href={PHONE_TEL} data-testid="mobile-nav-phone-link" className="flex items-center gap-2 text-charcoal">
                <Phone size={16} strokeWidth={1.75} /> {PHONE_DISPLAY}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
